import type {
  FullMenuCatalog,
  MenuOutOfStockParcial,
  ITurnOffProduct,
  TurnOnCustomization,
  TurnOnProduct,
  ITurnOffCustomization,
  ITurnOffCustomizationList,
  IMenuCustomizationGroup,
  IMenuCustomization,
  IMenuProduct,
  IMenuCategory,
} from 'features/menu-page/domain';
import type {
  FullMenuCatalogRawResp,
  OutOfStockRawResp,
} from './menuPage.response';
import type {
  TurnOffCustomizationReq,
  TurnOffProductReq,
  TurnOnCustomizationReq,
  TurnOnProductReq,
} from './menuPage.request';

function toFullMenu(menu: FullMenuCatalogRawResp): FullMenuCatalog {
  return {
    categories: menu.corridors
      .map(
        (c): IMenuCategory => ({
          id: c.corridor_id,
          name: c.corridor_name,
          index: c.index,
          storeId: c.store_id,
          description: c.description,
          isOn: true,
        }),
      )
      .toSorted((a, b) => a.index - b.index),
    products: menu.products
      .map(
        (p): IMenuProduct => ({
          id: p.id,
          storeId: p.store_id,
          categoryId: !!p.corridor_id ? p.corridor_id : NaN,
          index: p.index ?? p.product_index ?? 0,
          name: p.name ?? '',
          description: p.description ?? '',
          isOn: p.is_available,
          image: p.image?.endsWith('NO-IMAGE') ? '' : p.image ?? '',
          hasCustomizations:
            p.has_toppings && menu.toppings.some(tc => tc.product_id === p.id),
          suspensionInfo: {
            reason: null,
            shutdownDate: null,
            activationDate: null,
          },
        }),
      )
      .toSorted((a, b) => a.index - b.index),
    customizationGroups: menu.toppings
      .map(
        (tc): IMenuCustomizationGroup => ({
          id: tc.id,
          index: tc.index,
          productId: !!tc.product_id ? tc.product_id : NaN,
          description: tc.description ?? '',
          items: tc.toppings
            .map(
              (t): IMenuCustomization => ({
                id: t.id,
                index: t.index,
                description: t.description ?? '',
                isOn: t.activated,
                suspensionInfo: {
                  reason: null,
                  shutdownDate: null,
                  activationDate: null,
                },
              }),
            )
            .toSorted((a, b) => a.index - b.index),
        }),
      )
      .toSorted((a, b) => a.index - b.index),
  };
}

function toOutOfStock(outOfStock: OutOfStockRawResp): MenuOutOfStockParcial {
  const formatDate = (date: string | null) => date?.replace(' ', 'T') ?? null;
  return {
    products: outOfStock.product_out_of_stock.flatMap(category =>
      category.products_out_of_stock.map(p => ({
        id: p.id,
        categoryId: category.corridor_id,
        shutdownDate: formatDate(p.out_of_stock_creation_date),
        activationDate: formatDate(p.out_of_stock_activation_date),
      })),
    ),
    customizations: outOfStock.topping_out_of_stock.map(t => ({
      id: t.id,
      shutdownDate: formatDate(t.out_of_stock_creation_date),
      activationDate: formatDate(t.out_of_stock_activation_date),
    })),
  };
}

function toTurnOnProductReq(data: TurnOnProduct): TurnOnProductReq['payload'] {
  return {
    store_id: data.storeId,
    product_id: data.productId,
  };
}

function toTurnOffProductReq(
  data: ITurnOffProduct,
): TurnOffProductReq['payload'] {
  return {
    store_id: data.storeId,
    product_id: data.productId,
    deactivation_type: data.shutdownTimeOption,
    ...(data.shutdownTimeOption === 'CUSTOM' && {
      out_of_stock_activation_date: data.customDate.toLocaleDateString('en-CA'),
    }),
  };
}

function toTurnOnCustomizationReq(
  data: TurnOnCustomization,
): TurnOnCustomizationReq['payload'] {
  return {
    store_id: data.storeId,
    topping_id: data.customizationId,
  };
}

function toTurnOffCustomizationReq(
  data: ITurnOffCustomization,
): TurnOffCustomizationReq['payload'] {
  return {
    store_id: data.storeId,
    topping_id: data.customizationId,
    deactivation_type: data.shutdownTimeOption,
    ...(data.shutdownTimeOption === 'CUSTOM' && {
      out_of_stock_activation_date: data.customDate.toLocaleDateString('en-CA'),
    }),
  };
}

function toTurnOffCustomizationListReq(
  data: ITurnOffCustomizationList,
): TurnOffCustomizationReq['payload'] {
  return {
    store_id: data.storeId,
    topping_id: data.customizationIds,
    deactivation_type: data.shutdownTimeOption,
    ...(data.shutdownTimeOption === 'CUSTOM' && {
      out_of_stock_activation_date: data.customDate.toLocaleDateString('en-CA'),
    }),
  };
}

export const menuPageAdapter = {
  toFullMenu,
  toOutOfStock,
  toTurnOnProductReq,
  toTurnOffProductReq,
  toTurnOnCustomizationReq,
  toTurnOffCustomizationReq,
  toTurnOffCustomizationListReq,
};
